import { useEffect, useState, useMemo } from 'react';
// Models
import IReport from 'app/models/Report';
// Async
import { getReportPreviewUrl } from 'app/store/CaseReports/CaseReports.async';
// Actions
import MixpanelActions from 'app/features/mixpanel/MixpanelActions';
// Selectors
import { selectSortedReports } from 'app/store/CaseReports/CaseReports.selectors';
// Hooks
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Mui
import { Box } from '@mui/material';
// Components
import Dialog from 'app/components/Dialog';
import { Loader, Message } from 'app/components/Utilities';
import { useHistory, useLocation, useParams } from 'react-router-dom';

export default function ReportPreviewDialog() {
  const dispatch = useAppDispatch();
  // State
  const reports = useAppSelector(selectSortedReports)

  const { search } = useLocation();
  const { caseId } = useParams<{ caseId:string }>();
  const history = useHistory();

  const [reportId, setReportId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [reportUrl, setReportUrl] = useState<string | null>(null);

  const reportName = useMemo(() => {
    if ( !reports ) return null;
    const report = reports.find((report:IReport) => report.id === reportId);
    return report?.name;
  }, [reports, reportId]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const id = params.get('reportId');
    setReportId(id ? Number(id) : null);
  }, [search]);

  useEffect(() => {
    if ( !reportId ) return;

    const asyncGetReportPreviewUrl = async () => {
      try {
        setIsLoading(true);
        const reportUrl = await dispatch(getReportPreviewUrl(reportId)).unwrap();
        setReportUrl(reportUrl);
      } catch(error){
        console.error('Failed to fetch report preview URL:', error);
        setReportUrl(null);
      } finally {
        setIsLoading(false);
      }
    }
    asyncGetReportPreviewUrl();

    dispatch(MixpanelActions.handleCaseReportPreviewOpenedClick({ reportId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const handleClose = () => {
    history.push(`/admin/cases/${caseId}/reports`);
  };

  const renderReportPreview = () => {
    if ( isLoading ) return <Loader />;
    if ( !reportUrl ) return <Message text="Unable to load report preview. Please try again or contact support." />;
    return (
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1 }}>
        <embed
          src={reportUrl}
          type="application/pdf"
          style={{
            width: '100%',
            height: '100%'
          }}
        />
      </Box>
    )
  }

  if ( !reportId ) return null;

  return (
    <Dialog
      open
      onClose={handleClose}
      title={reportName}
      fullScreen
      ContentProps={{
        sx: {
          position: 'relative',
          p: 0,
          overflow: 'hidden'
        }
      }}
    >
      {renderReportPreview()}
    </Dialog>
  )
}
